import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FolderOpenOutlined, SaveOutlined, UserSwitchOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Tooltip,
  message
} from "antd";
import dayjs from "dayjs";
import { getSetting, loadRuntimeSettings } from "@core/runtime-settings";
import PhoneInput, { phoneRule } from "@components/sprs/pred/phone-input";
import SprPred from "@components/sprs/pred/spr-pred";
import { createSprPred, fetchSprPred, fetchSprPredList } from "@components/sprs/pred/spr-pred-api";
import calendarSrc from "./assets/calendar.svg";
import chevronSrc from "./assets/chevron-down.svg";
import {
  createHealthClient,
  fetchHealthClient,
  fetchHealthDoctors,
  fetchHealthSocGroups,
  saveHealthClient
} from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const SEX_OPTIONS = [
  { value: 1, label: "Муж" },
  { value: 0, label: "Жен" }
];

const CalendarIcon = () => (
  <span className="client__icon">
    <img src={calendarSrc} alt="" />
  </span>
);

const ChevronIcon = () => (
  <span className="client__icon">
    <img src={chevronSrc} alt="" />
  </span>
);

const toDayjs = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const dateOrNull = (value) => (value ? value.format("YYYY-MM-DD") : null);

const daysBetween = (beg, end) => {
  if (!beg || !end) return null;
  const a = dayjs(beg).startOf("day");
  const b = dayjs(end).startOf("day");
  if (!a.isValid() || !b.isValid()) return null;
  return b.diff(a, "day") + 1;
};

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const mapPredOptions = (data) => {
  const rows = Array.isArray(data) ? data : pick(data, "items", "Items") || [];
  return rows
    .map((row) => {
      const value = toNumber(pick(row, "idPred", "IdPred"));
      return value ? { value, label: pick(row, "name", "Name") || String(value) } : null;
    })
    .filter(Boolean);
};

/** Поля карточки, которые при привязке берутся из SPR_PRED (Delphi SetVisibledControls). */
const predToFormValues = (pred) => ({
  name: pick(pred, "name", "Name") ?? "",
  adres: pick(pred, "adress", "Adress") ?? "",
  phone: pick(pred, "phone", "Phone") ?? "",
  dateBird: toDayjs(pick(pred, "dateBird", "DateBird")),
  sex: toNumber(pick(pred, "sex", "Sex")),
  idSocGroup: toNumber(pick(pred, "idSg", "IdSg")) || undefined
});

const normalizeCard = (row) => ({
  idClient: toNumber(pick(row, "idClient", "IdClient")),
  idPred: toNumber(pick(row, "idPred", "IdPred")) ?? 0,
  isPred: toNumber(pick(row, "isPred", "IsPred")) ?? 0,
  predName: pick(row, "predName", "PredName") ?? "",
  docExpIdPred: toNumber(pick(row, "docExpIdPred", "DocExpIdPred")) ?? 0,
  name: pick(row, "name", "Name") ?? "",
  adres: pick(row, "adres", "Adres") ?? "",
  phone: pick(row, "phone", "Phone") ?? "",
  pasport: pick(row, "pasport", "Pasport") ?? "",
  dateBird: pick(row, "dateBird", "DateBird"),
  sex: toNumber(pick(row, "sex", "Sex")),
  idSotr: toNumber(pick(row, "idSotr", "IdSotr")),
  dateBeg: pick(row, "dateBeg", "DateBeg"),
  dateEnd: pick(row, "dateEnd", "DateEnd"),
  dateVipiska: pick(row, "dateVipiska", "DateVipiska"),
  vipiskaKomment: pick(row, "vipiskaKomment", "VipiskaKomment") ?? "",
  idSocGroup: toNumber(pick(row, "idSocGroup", "IdSocGroup")),
  idNumber: pick(row, "idNumber", "IdNumber") ?? "",
  komment: pick(row, "komment", "Komment") ?? "",
  dateVizit: pick(row, "dateVizit", "DateVizit"),
  osnTxt: pick(row, "osnTxt", "OsnTxt") ?? "",
  docnum: toNumber(pick(row, "docnum", "Docnum")),
  isNotSk: (toNumber(pick(row, "isNotSk", "IsNotSk")) ?? 0) === 1,
  ignoreProcCompatibility:
    (toNumber(pick(row, "ignoreProcCompatibility", "IgnoreProcCompatibility")) ?? 0) === 1,
  idDocExp: toNumber(pick(row, "idDocExp", "IdDocExp")) ?? 0,
  docExpNum: toNumber(pick(row, "docExpNum", "DocExpNum")) ?? 0
});

const cardToFormValues = (card) => ({
  name: card.name,
  adres: card.adres,
  phone: card.phone,
  pasport: card.pasport,
  dateBird: toDayjs(card.dateBird),
  sex: card.sex,
  idSotr: card.idSotr || undefined,
  dateBeg: toDayjs(card.dateBeg),
  dateEnd: toDayjs(card.dateEnd),
  days: daysBetween(card.dateBeg, card.dateEnd),
  dateVipiska: toDayjs(card.dateVipiska),
  vipiskaKomment: card.vipiskaKomment,
  idSocGroup: card.idSocGroup || undefined,
  idNumber: card.idNumber,
  komment: card.komment,
  dateVizit: toDayjs(card.dateVizit),
  osnTxt: card.osnTxt,
  docnum: card.docnum != null ? String(card.docnum) : "",
  isNotSk: card.isNotSk,
  ignoreProcCompatibility: card.ignoreProcCompatibility
});

/**
 * Вкладка «Основная» (Delphi TFrmESprClient / Основные).
 */
const ClientMain = ({ idClient, isNew = false, open, onSaved, onCancel }) => {
  const law = useSelector((state) => state.user.session?.law);
  const userId = useSelector((state) => state.user.session?.userId);
  const canChangeDoctor = getLawFlag(law, 3) === 1;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pred, setPred] = useState({ id: 0, name: "" });
  const [docExpIdPred, setDocExpIdPred] = useState(0);
  const [predOptions, setPredOptions] = useState([]);
  const [predLoading, setPredLoading] = useState(false);
  const [predBusy, setPredBusy] = useState(false);
  const [predEditOpen, setPredEditOpen] = useState(false);
  const predTimerRef = useRef(0);
  const [docExp, setDocExp] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [socGroups, setSocGroups] = useState([]);
  const [runtimeSettings, setRuntimeSettings] = useState(null);

  const dateBeg = Form.useWatch("dateBeg", form);
  const dateEnd = Form.useWatch("dateEnd", form);
  const nameValue = Form.useWatch("name", form);
  const displayName = String(nameValue || "").trim() || (isNew ? "Новый пациент" : "");

  const doctorOptions = useMemo(
    () =>
      (doctors || []).map((d) => ({
        value: d.idSotr ?? d.IdSotr,
        label: d.name ?? d.Name ?? ""
      })),
    [doctors]
  );

  const socGroupOptions = useMemo(
    () =>
      (socGroups || []).map((g) => ({
        value: g.id ?? g.Id,
        label: g.name ?? g.Name ?? ""
      })),
    [socGroups]
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([
      fetchHealthDoctors(),
      fetchHealthSocGroups(),
      loadRuntimeSettings().catch(() => ({}))
    ])
      .then(([docs, groups, settings]) => {
        if (cancelled) return;
        setDoctors(Array.isArray(docs) ? docs : []);
        setSocGroups(Array.isArray(groups) ? groups : []);
        setRuntimeSettings(settings);
      })
      .catch(() => {
        if (cancelled) return;
        setDoctors([]);
        setSocGroups([]);
        setRuntimeSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || runtimeSettings == null) return undefined;

    if (isNew) {
      setLoading(false);
      setError("");
      setPred({ id: 0, name: "" });
      setDocExpIdPred(0);
      setDocExp(null);
      form.resetFields();
      const today = dayjs().startOf("day");
      form.setFieldsValue({
        name: "",
        adres: "",
        phone: "",
        pasport: "",
        dateBird: null,
        sex: undefined,
        idSotr: !canChangeDoctor && userId ? userId : undefined,
        dateBeg: today,
        dateEnd: today,
        days: 1,
        dateVipiska: null,
        vipiskaKomment: "",
        idSocGroup: undefined,
        idNumber: "",
        komment: "",
        dateVizit: null,
        osnTxt: "",
        docnum: "",
        isNotSk: false,
        ignoreProcCompatibility: false
      });
      return undefined;
    }

    if (!idClient) return undefined;

    let cancelled = false;
    setLoading(true);
    setError("");
    form.resetFields();

    fetchHealthClient(idClient)
      .then((data) => {
        if (cancelled) return;
        const card = normalizeCard(data);
        setPred({ id: card.idPred, name: card.predName });
        setDocExpIdPred(card.docExpIdPred);
        setDocExp(card.idDocExp > 0 ? { id: card.idDocExp, num: card.docExpNum } : null);
        form.setFieldsValue(cardToFormValues(card));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Не удалось загрузить клиента");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, idClient, isNew, form, canChangeDoctor, userId, runtimeSettings]);

  useEffect(() => {
    if (loading) return;
    const days = daysBetween(dateBeg, dateEnd);
    const current = form.getFieldValue("days");
    if (days !== current) {
      form.setFieldValue("days", days);
    }
  }, [dateBeg, dateEnd, form, loading]);

  const onDaysChange = (days) => {
    const beg = form.getFieldValue("dateBeg");
    if (!beg || !Number.isFinite(days) || days < 1) {
      form.setFieldValue("days", days);
      return;
    }
    form.setFieldsValue({
      days,
      dateEnd: dayjs(beg).startOf("day").add(days - 1, "day")
    });
  };

  useEffect(() => () => clearTimeout(predTimerRef.current), []);

  const searchPreds = (q) => {
    clearTimeout(predTimerRef.current);
    predTimerRef.current = setTimeout(() => {
      setPredLoading(true);
      fetchSprPredList({ q, take: 30 })
        .then((data) => setPredOptions(mapPredOptions(data)))
        .catch(() => setPredOptions([]))
        .finally(() => setPredLoading(false));
    }, 250);
  };

  /** Привязать контрагента: поля ФИО/адрес/телефон/дата рождения/пол/соц. группа берутся из справочника. */
  const applyPred = async (idPred) => {
    setPredBusy(true);
    try {
      const data = await fetchSprPred(idPred);
      form.setFieldsValue(predToFormValues(data));
      setPred({ id: idPred, name: pick(data, "name", "Name") || "" });
    } catch (e) {
      message.error(e.message || "Не удалось загрузить контрагента");
    } finally {
      setPredBusy(false);
    }
  };

  /** Отвязать: значения остаются в полях и снова редактируются (Delphi BtnPredClear). */
  const clearPred = () => setPred({ id: 0, name: "" });

  /** Сохранить данные пациента новым контрагентом и привязать (Delphi BtnSaveToSpr → TFrmESprPred.NewFromData). */
  const saveToSpr = async () => {
    const values = form.getFieldsValue(["name", "adres", "phone", "pasport", "dateBird", "sex", "idSocGroup"]);
    if (!(values.name || "").trim()) {
      message.warning("Укажите ФИО");
      return;
    }
    setPredBusy(true);
    try {
      const saved = await createSprPred({
        vid: 0,
        name: values.name.trim(),
        adress: values.adres || "",
        phone: values.phone || "",
        pasport: values.pasport || "",
        dateBird: dateOrNull(values.dateBird),
        sex: values.sex ?? 0,
        idSg: values.idSocGroup || 0
      });
      const id = toNumber(pick(saved, "idPred", "IdPred"));
      if (id) {
        setPred({ id, name: pick(saved, "name", "Name") || values.name.trim() });
        message.success("Контрагент сохранён в справочник");
      }
    } catch (e) {
      message.error(e.message || "Не удалось сохранить в справочник");
    } finally {
      setPredBusy(false);
    }
  };

  const predSelectOptions =
    pred.id && !predOptions.some((o) => o.value === pred.id)
      ? [{ value: pred.id, label: pred.name || `Контрагент №${pred.id}` }, ...predOptions]
      : predOptions;

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = {
      idPred: pred.id || 0,
      name: values.name?.trim() || "",
      adres: values.adres?.trim() || "",
      phone: values.phone?.trim() || "",
      pasport: values.pasport?.trim() || "",
      dateBird: dateOrNull(values.dateBird),
      sex: values.sex,
      idSotr: values.idSotr,
      dateBeg: dateOrNull(values.dateBeg),
      dateEnd: dateOrNull(values.dateEnd),
      dateVipiska: dateOrNull(values.dateVipiska),
      vipiskaKomment: values.vipiskaKomment?.trim() || "",
      idSocGroup: values.idSocGroup || null,
      idNumber: values.idNumber?.trim() || "",
      komment: values.komment?.trim() || "",
      dateVizit: values.dateVizit ? values.dateVizit.toISOString() : null,
      osnTxt: values.osnTxt?.trim() || "",
      docnum: (() => {
        const raw = values.docnum?.trim();
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })(),
      isNotSk: values.isNotSk ? 1 : 0,
      ignoreProcCompatibility: values.ignoreProcCompatibility ? 1 : 0
    };

    try {
      if (isNew) {
        const created = await createHealthClient(body);
        onSaved?.(normalizeCard(created));
      } else {
        await saveHealthClient(idClient, body);
        onSaved?.();
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить клиента");
    } finally {
      setSaving(false);
    }
  };

  const stayLocked = !isNew && !!docExp;
  const isPred = pred.id > 0;

  const doctorRequired =
    Number(getSetting(runtimeSettings, "CONST_VRACH_NOT_NECESSARY", 0)) !== 1;
  // PHONE_VID = 1: ввод по маске +7 XXX XXX XX XX (как в карточке контрагента), иначе свободный формат.
  const validatePhone = Number(getSetting(runtimeSettings, "PHONE_VID", 0)) === 1;

  return (
    <div className="client-tab client-tab--main">
      {error ? <Alert className="client__error" type="error" showIcon message={error} /> : null}
      {displayName ? <div className="client__banner">{displayName}</div> : null}
      <div className="client-tab__body">
        {loading || runtimeSettings == null ? (
          <div className="client__center">
            <Spin tip="Загрузка клиента..." />
          </div>
        ) : (
          <Form
            form={form}
            className="client__form"
            layout="vertical"
            colon={false}
            disabled={saving}
            onFinish={onFinish}
            requiredMark={false}
          >
            <div className="client__cols">
              <div className="client__col client__col--left">
                <Form.Item label="Контрагент">
                  <div className="client__pred-row">
                    <Select
                      className="client__pred-select"
                      showSearch
                      allowClear
                      value={pred.id || undefined}
                      options={predSelectOptions}
                      filterOption={false}
                      onSearch={searchPreds}
                      onOpenChange={(opened) => opened && searchPreds("")}
                      loading={predLoading}
                      disabled={saving || predBusy}
                      placeholder="Не привязан — выберите из справочника"
                      notFoundContent={predLoading ? "Загрузка…" : "Не найдено"}
                      onChange={(value) => (value ? applyPred(value) : clearPred())}
                      suffixIcon={<ChevronIcon />}
                    />
                    <Space size={0}>
                      {isPred ? (
                        <Tooltip title="Открыть карточку контрагента">
                          <Button
                            type="text"
                            size="small"
                            icon={<FolderOpenOutlined />}
                            aria-label="Открыть контрагента"
                            disabled={predBusy}
                            onClick={() => setPredEditOpen(true)}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Сохранить пациента в справочник контрагентов">
                          <Button
                            type="text"
                            size="small"
                            icon={<SaveOutlined />}
                            aria-label="В справочник"
                            loading={predBusy}
                            onClick={saveToSpr}
                          />
                        </Tooltip>
                      )}
                      {!isPred && docExpIdPred > 0 ? (
                        <Tooltip title="Привязать контрагента из путёвки">
                          <Button
                            type="text"
                            size="small"
                            icon={<UserSwitchOutlined />}
                            aria-label="Из путёвки"
                            disabled={predBusy}
                            onClick={() => applyPred(docExpIdPred)}
                          />
                        </Tooltip>
                      ) : null}
                    </Space>
                  </div>
                </Form.Item>

                <Form.Item
                  name="name"
                  label={
                    <>
                      ФИО<span className="client__req"> *</span>
                    </>
                  }
                  rules={isPred ? undefined : [{ required: true, message: "Укажите ФИО" }]}
                >
                  <Input disabled={isPred} />
                </Form.Item>

                <Form.Item name="adres" label="Адрес">
                  <Input disabled={isPred} placeholder="Введите адрес проживания" />
                </Form.Item>

                <div className="client__grid-2">
                  <Form.Item name="dateBird" label="Дата рождения">
                    <DatePicker
                      format="DD.MM.YYYY"
                      placeholder="дд.мм.гггг"
                      allowClear
                      disabled={isPred}
                      suffixIcon={<CalendarIcon />}
                    />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label="Телефон"
                    rules={validatePhone ? [phoneRule] : undefined}
                    validateTrigger="onBlur"
                  >
                    {validatePhone ? <PhoneInput disabled={isPred} /> : <Input disabled={isPred} />}
                  </Form.Item>
                </div>

                <div className="client__grid-2">
                  <Form.Item name="sex" label="Пол">
                    <Select
                      options={SEX_OPTIONS}
                      allowClear
                      disabled={isPred}
                      suffixIcon={<ChevronIcon />}
                    />
                  </Form.Item>
                  <Form.Item name="dateVizit" label="Первый визит">
                    <DatePicker
                      format="DD.MM.YYYY"
                      placeholder="дд.мм.гггг"
                      allowClear
                      suffixIcon={<CalendarIcon />}
                    />
                  </Form.Item>
                </div>

                <div className="client__stay">
                  <div className="client__stay-title">Сроки пребывания</div>
                  <div className="client__stay-row">
                    <Form.Item
                      name="dateBeg"
                      label="Начало"
                      rules={[{ required: true, message: "Укажите дату начала" }]}
                    >
                      <DatePicker
                        format="DD.MM.YYYY"
                        allowClear={false}
                        disabled={stayLocked}
                        suffixIcon={<CalendarIcon />}
                      />
                    </Form.Item>
                    <Form.Item name="days" label="Срок, дней" className="client__stay-days">
                      <InputNumber
                        min={1}
                        controls={false}
                        onChange={onDaysChange}
                        disabled={stayLocked}
                      />
                    </Form.Item>
                    <Form.Item
                      name="dateEnd"
                      label="Окончание"
                      rules={[{ required: true, message: "Укажите дату окончания" }]}
                    >
                      <DatePicker
                        format="DD.MM.YYYY"
                        placeholder="дд.мм.гггг"
                        allowClear={false}
                        disabled={stayLocked}
                        suffixIcon={<CalendarIcon />}
                      />
                    </Form.Item>
                  </div>
                  {stayLocked ? (
                    <div className="client__stay-hint">
                      Срок пребывания задан путёвкой № {docExp.num} и изменяется только в ней
                    </div>
                  ) : null}
                  <Form.Item name="isNotSk" valuePropName="checked" className="client__check">
                    <Checkbox>Нет санаторной карты</Checkbox>
                  </Form.Item>
                </div>

                <Form.Item name="pasport" label="Паспорт">
                  <Input placeholder="Серия, номер, кем и когда выдан" />
                </Form.Item>

                <Form.Item name="docnum" hidden>
                  <Input />
                </Form.Item>
              </div>

              <div className="client__col client__col--right">
                <Form.Item name="vipiskaKomment" label="Причина досрочной выписки">
                  <Input placeholder="Не указано" />
                </Form.Item>

                <Form.Item name="dateVipiska" label="Дата выписки">
                  <DatePicker
                    format="DD.MM.YYYY"
                    placeholder="дд.мм.гггг"
                    allowClear
                    suffixIcon={<CalendarIcon />}
                  />
                </Form.Item>

                <Form.Item
                  name="ignoreProcCompatibility"
                  valuePropName="checked"
                  className="client__check"
                >
                  <Checkbox>Игнорировать совместимость процедур</Checkbox>
                </Form.Item>

                <Form.Item name="idSocGroup" label="Социальная группа">
                  <Select
                    options={socGroupOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    disabled={isPred}
                    placeholder="Выберите группу"
                    suffixIcon={<ChevronIcon />}
                  />
                </Form.Item>

                <Form.Item name="idNumber" label="Проживает в номере">
                  <Input placeholder="Номер проживания" />
                </Form.Item>

                <Form.Item name="osnTxt" label="Основание" className="client__textarea">
                  <Input.TextArea
                    rows={3}
                    placeholder="Дополнительные примечания по лечению или питанию..."
                  />
                </Form.Item>

                <Form.Item
                  name="idSotr"
                  label="Лечащий врач"
                  rules={
                    doctorRequired
                      ? [{ required: true, message: "Укажите лечащего врача" }]
                      : undefined
                  }
                >
                  <Select
                    options={doctorOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    disabled={!canChangeDoctor}
                    placeholder="Выберите врача"
                    suffixIcon={<ChevronIcon />}
                  />
                </Form.Item>

                <Form.Item name="komment" label="Комментарий" className="client__textarea">
                  <Input.TextArea rows={2} placeholder="Дополнительная системная информация" />
                </Form.Item>
              </div>
            </div>
          </Form>
        )}
      </div>
      <SprPred
        open={predEditOpen}
        idPred={pred.id || null}
        onClose={() => setPredEditOpen(false)}
        onSaved={() => {
          setPredEditOpen(false);
          if (pred.id) applyPred(pred.id);
        }}
      />
      <div className="client__footer">
        <Button
          className="client__btn client__btn--primary"
          type="primary"
          loading={saving}
          disabled={loading}
          onClick={() => form.submit()}
        >
          Сохранить
        </Button>
        <Button className="client__btn client__btn--ghost" onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default ClientMain;
